import { purchaseOrderItemOrderBy, ChangeRequestOrdering } from '@/app-constants';
import { IPostHoldRequestRequest } from '@/interfaces/api-requests/post-hold-request-request';
import * as ApiModel from './interfaces/api-model';
import { IGetPurchaseOrderHoldRequestsRequest } from '@/interfaces/api-requests/get-purchase-order-hold-requests-request';
import { IPutHoldRequestRequest } from '@/interfaces/api-requests/put-hold-request-request';
import { IGetHoldRequestItemsRequest } from '@/interfaces/api-requests/get-hold-request-items-request';
import { GatewayApi } from '@/app/common/apis/gatewayApi';
import { ISupplierConfirmation } from '../../../../interfaces/supplier-confirmation';
import { IPoItem } from './interfaces/po-item';
import { IPagedCollection } from '../list/interfaces/po-response';
import { ItemStateEnum } from './components/change-requests/ItemStateEnum';

export interface IGetPurchaseOrderVerificationDetails {
  purchaseOrderId: number;
}
export const getOrderVerificationDetails = async (request: IGetPurchaseOrderVerificationDetails) => {
  const response = await GatewayApi.get(
    `/PurchaseOrders/OrderVerificationDetails/${request.purchaseOrderId}`,
    { params: request }
  );
  return response.data;
};

export interface IGetPoDetailsBucketTotals {
  poId: number;
  recipient: string;
  searchText: string;
}
export const getPoDetailsBucketTotals = async (request: IGetPoDetailsBucketTotals) => {
  const uri = `/PurchaseOrders/${request.poId}/ItemsBuckets`;
  const response = await GatewayApi.get(uri, {
    params: {
      recipient: request.recipient,
      searchText: request.searchText
    }
  });

  return response.data;
};

export const getPoItems = async (request: ApiModel.IGetPoItemsRequest): Promise<ApiModel.IGetPoItemsResponse> => {
  const uri = `/PurchaseOrders/${request.poId}/ItemsBuckets/${request.bucketType}/Items`;
  const response = await GatewayApi.post(uri, {
    purchaseOrderId: request.poId,
    orderBy: request.orderBy || purchaseOrderItemOrderBy.PurchaseOrderItemId,
    pageNumber: request.pageNumber,
    pageSize: request.pageSize,
    searchText: request.searchText
  });

  return response.data;
};

export const postApprovePo = async (request) => {
  const { poId, bucketType, ...apiRequest } = request;
  const response = await GatewayApi.post(`/PurchaseOrders/Acknowledgement/${request.poId}`, apiRequest);
  return response.data;
};

export interface IPostChangeRequestResponse {
  changeRequestId: number;
  conversationId: number;
  changeRequestAlreadyExistsForItems: boolean;
}
export const createChangeRequest = async (request: ApiModel.IPostChangeRequestRequest) => {
  const uri = `/PurchaseOrders/${request.purchaseOrderId}/ChangeRequests`;
  const response = await GatewayApi.post<IPostChangeRequestResponse>(uri, request);
  return response.data;
};

export const createHoldRequest = async (request: IPostHoldRequestRequest) => {
  const uri = `/PurchaseOrders/${request.purchaseOrderId}/HoldRequests`;
  const response = await GatewayApi.post(uri, request);
  return response.data;
};

export const getHoldRequests = async (request: IGetPurchaseOrderHoldRequestsRequest) => {
  const uri = `/PurchaseOrders/${request.purchaseOrderId}/HoldRequests`;
  const response = await GatewayApi.get(uri, { params: request });
  return response.data.holdRequests;
};

export const getHoldRequestItems = async (request: IGetHoldRequestItemsRequest) => {
  const uri = `/PurchaseOrders/${request.purchaseOrderId}/HoldRequests/${request.holdRequestId}/Items`;
  const response = await GatewayApi.get(uri, { params: request });
  return response.data;
};

export const putHoldRequest = async (request: IPutHoldRequestRequest) => {
  const uri = `/PurchaseOrders/${request.purchaseOrderId}/HoldRequests/${request.holdRequestId}`;
  await GatewayApi.put(uri, request);
};

export const putSupplierEditChangeRequest = async (request) => {
  const uri = `/ChangeRequests/${request.changeRequestId}/SupplierUpdate`;
  const response = await GatewayApi.put(uri, request);
  return response.data;
};

export const putBuyerApproveChangeRequest = async (request) => {
  const uri = `/ChangeRequests/${request.changeRequestId}/BuyerConfirmation`;
  const response = await GatewayApi.put(uri, request);

  return response.data;
};

export const putSupplierApproveChangeRequest = async (request) => {
  const uri = `/ChangeRequests/${request.changeRequestId}/SupplierConfirmation`;
  const response = await GatewayApi.put(uri, request);

  return response.data;
};

export const getChangeRequestItems = async ({ poId, crId, ...body }) => {
  const uri = `/ChangeRequests/${poId}/ChangeRequests/${crId}/Items`;
  const response = await GatewayApi.post(uri, body);
  return response.data;
};

export interface ICancelChangeRequestRequestModel {
  changeRequestId: number;
  itemState: ItemStateEnum;
}

export interface ICancelChangeRequestResponseModel extends ICancelChangeRequestRequestModel {
  purchaseOrderItemCount: number;
  hasUnread: boolean;
}

export const cancelChangeRequest = (request: ICancelChangeRequestRequestModel) => {
  return GatewayApi.put(
    `/ChangeRequests/${request.changeRequestId}/SupplierCancel`,
    // TODO: Investigate capital case json. Should be camel case.
    {
      ChangeRequestId: request.changeRequestId,
      RequestedItemState: request.itemState
    }
  );
};

export interface IGetOrderVerificationDetailsChangeRequests {
  bucketTypeId?: number;
  changeRequestOrdering?: ChangeRequestOrdering;
  customerCode?: string;
  supplierCode?: string;
  filterByUnreadMessages?: boolean;
  pageNumber: number;
  pageSize: number;
  poId: number;
}
export const getOrderVerificationDetailsChangeRequests = async (request: IGetOrderVerificationDetailsChangeRequests) => {
  const uri = `/PurchaseOrders/OrderVerificationDetails/${request.poId}/ChangeRequests/${request.pageNumber}/${request.pageSize}`;
  const response = await GatewayApi.get(uri, { params: request });

  return response.data.changeRequests;
};

export const deletePoConversationFollowup = async (request: ApiModel.IDeletePoConversationFollowup): Promise<void> => {
  const uri = `/PurchaseOrders/${request.purchaseOrderId}/Followup`;
  await GatewayApi.delete(uri, { params: request });
};

export const putPoConversationFollowup = async (request: ApiModel.IPutPoConversationFollowup): Promise<void> => {
  const uri = `/PurchaseOrders/${request.purchaseOrderId}/Followup`;
  await GatewayApi.put(uri, request);
};

export interface IGetSupplierConfirmations {
  purchaseOrderId: number;
};

interface ISupplierConfirmationFromResponse {
  id: string;
  requestedConfirmationDate?: string;
  actualConfirmationDate: string;
  requestedBy: string;
  confirmedBy: string;
  supplierConfirmationKindId: string;
  purchaseOrderId: string;
}
export interface IGetSupplierConfirmationsResponse {
  supplierConfirmations: ISupplierConfirmationFromResponse[];
};
function convertJsonSupplierConfirmationToSupplierConfirmation(jsonSupplierConfirmation: ISupplierConfirmationFromResponse): ISupplierConfirmation {
  return {
    id: parseInt(jsonSupplierConfirmation.id, 10),
    requestedConfirmationDate: jsonSupplierConfirmation.requestedConfirmationDate ? new Date(jsonSupplierConfirmation.requestedConfirmationDate) : undefined,
    actualConfirmationDate: new Date(jsonSupplierConfirmation.actualConfirmationDate),
    requestedBy: jsonSupplierConfirmation.requestedBy,
    confirmedBy: jsonSupplierConfirmation.confirmedBy,
    supplierConfirmationKindId: parseInt(jsonSupplierConfirmation.supplierConfirmationKindId, 10),
  };
}

export const getSupplierConfirmations = async (request: IGetSupplierConfirmations): Promise<{ supplierConfirmations: ISupplierConfirmation[]; }> => {
  const uri = `/PurchaseOrders/${request.purchaseOrderId}/SupplierConfirmations`;
  const response = await GatewayApi.get<IGetSupplierConfirmationsResponse>(uri);
  const supplierConfirmations = response.data.supplierConfirmations.map<ISupplierConfirmation>(convertJsonSupplierConfirmationToSupplierConfirmation);
  return {
    supplierConfirmations
  };
};


export interface IGetSupplierConfirmationItems {
  supplierConfirmationId: number;
  pageNumber: number;
  pageSize: number;
}
export interface IGetSupplierConfirmationItemsResponse {
  purchaseOrderItems: IPagedCollection<IPoItem>;
}
export const getSupplierConfirmationItems = async (request: IGetSupplierConfirmationItems): Promise<IGetSupplierConfirmationItemsResponse> => {
  const { supplierConfirmationId, ...params } = request;

  const uri = `/SupplierConfirmations/${supplierConfirmationId}/Items`;
  const response = await GatewayApi.get(uri, { params });
  return response.data;
};
